import { summarizeRaidStrategyProgress } from './raidStrategyProgress.js';
import { strategyPlanTargets } from './raidStrategyRecommendation.js';
import {
  canonicalStrategyDeck,
  reconcileRaidStrategy,
  strategyMemberOpenid,
} from './raidStrategyReconciliation.js';

const EPSILON = 0.000001;
const MAX_VISITED_ALLOCATIONS = 20000;

function textValue(value) {
  return String(value ?? '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function planKey(bossNo, phaseNo) {
  return `${numberValue(bossNo)}:${numberValue(phaseNo)}`;
}

function isManualId(id) {
  return textValue(id).startsWith('manual:');
}

function optionFromCandidate(candidate) {
  const deck = Array.isArray(candidate?.deck) ? candidate.deck : [];
  return {
    id: textValue(candidate?.id),
    source: 'practice',
    nickname: textValue(candidate?.nickname),
    title: textValue(candidate?.title) || `모의전 ${numberValue(candidate?.slotNo, 1)}`,
    slotNo: numberValue(candidate?.slotNo, 1),
    bossNo: numberValue(candidate?.bossNo),
    damageEok: numberValue(candidate?.damageEok),
    deck,
    codes: canonicalStrategyDeck(deck),
    measured: true,
    isCurrent: false,
  };
}

function optionFromPlanItem(item) {
  const deck = Array.isArray(item?.deck) ? item.deck : [];
  return {
    id: textValue(item?.practiceId),
    source: isManualId(item?.practiceId) ? 'manual' : 'practice',
    nickname: textValue(item?.nickname),
    title: isManualId(item?.practiceId) ? '직접 입력' : textValue(item?.title) || `모의전 ${numberValue(item?.slotNo, 1)}`,
    slotNo: numberValue(item?.slotNo, 1),
    bossNo: numberValue(item?.bossNo),
    damageEok: numberValue(item?.damageEok),
    deck,
    codes: canonicalStrategyDeck(deck),
    measured: !isManualId(item?.practiceId),
    isCurrent: true,
  };
}

// 1~3단 보스는 남은 체력만큼만 기록되고 그 위로 때린 딜은 사라진다. 4단은 5보스 하나뿐이고 체력이
// 무한이라 때린 만큼 전부 기록된다. 순위는 1~3단과 4단에 기록된 피해량의 총합이므로, 1~3단을 낭비 없이
// 클리어하고 남은 최고 덱을 4단에 쓰는 것이 이득이다.
export const RAID_INFINITE_PHASE_NO = 4;
export const RAID_INFINITE_BOSS_NO = 5;

export function isInfiniteRaidTarget(bossNo, phaseNo) {
  return numberValue(phaseNo) === RAID_INFINITE_PHASE_NO && numberValue(bossNo) === RAID_INFINITE_BOSS_NO;
}

// 체력을 넘겨 때린 만큼은 기록되지 않으므로, 이 멤버의 딜 중 실제로 점수가 된 몫만 센다. 목표 체력을
// 모르는 칸(0)은 자를 근거가 없으니 자르지 않는다.
function creditedDamage({ baseEok, addedEok, targetHpEok, uncapped }) {
  if (uncapped || targetHpEok <= EPSILON) return addedEok;
  return Math.max(0, Math.min(baseEok + addedEok, targetHpEok) - Math.min(baseEok, targetHpEok));
}

function targetState(slotTargets, choices) {
  const damageByPlan = new Map();
  for (const choice of choices) {
    damageByPlan.set(choice.slot.planKey, (damageByPlan.get(choice.slot.planKey) || 0) + choice.option.damageEok);
  }
  return slotTargets.map((target) => {
    const memberEok = damageByPlan.get(target.planKey) || 0;
    const projectedEok = target.baseEok + memberEok;
    const uncapped = Boolean(target.isInfinite);
    const covered = uncapped || (target.targetHpEok > EPSILON && projectedEok + EPSILON >= target.targetHpEok);
    return {
      ...target,
      memberEok,
      projectedEok,
      covered,
      overkillEok: uncapped || !covered ? 0 : Math.max(0, projectedEok - target.targetHpEok),
      shortageEok: covered ? 0 : Math.max(0, target.targetHpEok - projectedEok),
      memberEffectiveEok: creditedDamage({
        baseEok: target.baseEok,
        addedEok: memberEok,
        targetHpEok: target.targetHpEok,
        uncapped,
      }),
    };
  });
}

function buildAllocation(choices, slotTargets) {
  const targets = targetState(slotTargets, choices);
  return {
    key: choices.map((choice) => `${choice.slot.key}=${choice.option.id}`).join('|'),
    choices: choices.map((choice) => ({ slotKey: choice.slot.key, option: choice.option })),
    optionBySlot: new Map(choices.map((choice) => [choice.slot.key, choice.option])),
    memberTotalEok: choices.reduce((sum, choice) => sum + choice.option.damageEok, 0),
    memberEffectiveEok: targets.reduce((sum, target) => sum + target.memberEffectiveEok, 0),
    codePlacement: new Map(choices.flatMap((choice) => choice.option.codes.map((code) => [code, choice.slot.key]))),
    targets,
    coveredCount: targets.filter((target) => target.covered).length,
    shortageSumEok: targets.reduce((sum, target) => sum + target.shortageEok, 0),
    overkillSumEok: targets.reduce((sum, target) => sum + target.overkillEok, 0),
    wastedEok: choices.reduce((sum, choice) => sum + choice.option.damageEok, 0)
      - targets.reduce((sum, target) => sum + target.memberEffectiveEok, 0),
  };
}

// Same order the raid itself rewards. Clearing 1~3 comes first because it is the gate to the infinite
// stage; after that the union is scored on damage that was actually recorded, which is exactly what
// effective damage measures — raw total only breaks a tie between allocations that record the same.
export function compareAllocationRank(left, right) {
  if (left.coveredCount !== right.coveredCount) return right.coveredCount - left.coveredCount;
  if (Math.abs(left.memberEffectiveEok - right.memberEffectiveEok) > EPSILON) {
    return right.memberEffectiveEok - left.memberEffectiveEok;
  }
  if (Math.abs(left.shortageSumEok - right.shortageSumEok) > EPSILON) return left.shortageSumEok - right.shortageSumEok;
  if (Math.abs(left.overkillSumEok - right.overkillSumEok) > EPSILON) return left.overkillSumEok - right.overkillSumEok;
  return right.memberTotalEok - left.memberTotalEok;
}

// Every way this member could spread their registered practice decks over the attacks the plan
// already gives them, honouring the rule that drives the whole question: one Nikke can only be used
// once across a member's attacks, so putting 아니스 : 스타 on boss 1 takes her away from boss 3.
function enumerateAllocations(slots, optionsBySlot, slotTargets, blockedCodes) {
  const allocations = [];
  let visited = 0;

  const walk = (index, chosen, usedIds, usedCodes) => {
    if (visited > MAX_VISITED_ALLOCATIONS) return;
    if (index >= slots.length) {
      visited += 1;
      allocations.push(buildAllocation(chosen, slotTargets));
      return;
    }
    const slot = slots[index];
    for (const option of optionsBySlot.get(slot.key) || []) {
      if (usedIds.has(option.id)) continue;
      if (option.codes.some((code) => usedCodes.has(code))) continue;
      if (!option.isCurrent && option.codes.some((code) => blockedCodes.has(code))) continue;
      usedIds.add(option.id);
      option.codes.forEach((code) => usedCodes.add(code));
      chosen.push({ slot, option });
      walk(index + 1, chosen, usedIds, usedCodes);
      chosen.pop();
      option.codes.forEach((code) => usedCodes.delete(code));
      usedIds.delete(option.id);
    }
  };

  walk(0, [], new Set(), new Set());
  return allocations;
}

function contestedCodeRows({ slots, optionsBySlot, allocations, baseline }) {
  const slotsByCode = new Map();
  for (const slot of slots) {
    for (const option of optionsBySlot.get(slot.key) || []) {
      for (const code of option.codes) {
        const set = slotsByCode.get(code) || new Set();
        set.add(slot.key);
        slotsByCode.set(code, set);
      }
    }
  }

  const rows = [];
  for (const [code, slotKeys] of slotsByCode.entries()) {
    if (slotKeys.size < 2) continue;
    const placements = [];

    for (const slotKey of slotKeys) {
      const best = allocations
        .filter((allocation) => allocation.codePlacement.get(code) === slotKey)
        .sort(compareAllocationRank)[0] || null;
      placements.push({
        slotKey,
        allocation: best,
        // Which other slot makes this placement impossible: it has no deck without this Nikke.
        blockedBy: best ? [] : slots
          .filter((slot) => slot.key !== slotKey
            && (optionsBySlot.get(slot.key) || []).every((option) => option.codes.includes(code)))
          .map((slot) => slot.key),
      });
    }

    const withoutBest = allocations
      .filter((allocation) => !allocation.codePlacement.has(code))
      .sort(compareAllocationRank)[0] || null;
    placements.push({
      slotKey: '',
      allocation: withoutBest,
      blockedBy: withoutBest ? [] : slots
        .filter((slot) => (optionsBySlot.get(slot.key) || []).every((option) => option.codes.includes(code)))
        .map((slot) => slot.key),
    });

    const totals = placements.map((placement) => placement.allocation?.memberEffectiveEok).filter((value) => Number.isFinite(value));
    rows.push({
      code,
      currentSlotKey: baseline?.codePlacement.get(code) || '',
      placements,
      buildableCount: totals.length,
      spreadEok: totals.length > 1 ? Math.max(...totals) - Math.min(...totals) : 0,
    });
  }

  // A Nikke the leader can actually move comes first: rows where only today's placement is possible
  // explain a constraint, not a choice.
  return rows.sort((left, right) => (right.buildableCount > 1) - (left.buildableCount > 1)
    || right.spreadEok - left.spreadEok
    || left.code.localeCompare(right.code));
}

// The member's attacks as the plan has them now, what else each of their decks could be, and what
// every valid rearrangement would do to the bosses they are planned on.
//
// `plans` must already be the active plans (completed targets emptied); `candidates` is the practice
// pool, which buildTetrisCandidates has already stripped of decks that clash with real attacks.
export function buildMemberPlacementComparison({
  memberOpenid,
  plans = [],
  actualRecords = [],
  candidates = [],
  focus = null,
} = {}) {
  const openid = textValue(memberOpenid);
  if (!openid) return null;

  const planList = Array.isArray(plans) ? plans : [];
  const reconciled = reconcileRaidStrategy({ plans: planList, actualRecords });
  const memberAttacks = reconciled.effectiveAttacks.filter((attack) => strategyMemberOpenid(attack) === openid);
  const actualAttacks = memberAttacks
    .filter((attack) => attack.sourceType === 'actual')
    .map((attack) => ({
      bossNo: numberValue(attack.bossNo),
      phaseNo: numberValue(attack.phaseNo),
      attackNo: numberValue(attack.attackNo, 0) || null,
      damageEok: numberValue(attack.damageEok),
      deck: Array.isArray(attack.deck) ? attack.deck : [],
      codes: canonicalStrategyDeck(attack.deck),
    }));
  const blockedCodes = new Set(actualAttacks.flatMap((attack) => attack.codes));

  const slots = memberAttacks
    .filter((attack) => attack.sourceType === 'plan')
    .map((attack) => ({
      key: `${planKey(attack.bossNo, attack.phaseNo)}#${numberValue(attack.planIndex)}`,
      planKey: planKey(attack.bossNo, attack.phaseNo),
      bossNo: numberValue(attack.bossNo),
      phaseNo: numberValue(attack.phaseNo),
      planIndex: numberValue(attack.planIndex),
      current: optionFromPlanItem(attack),
    }))
    .sort((left, right) => left.phaseNo - right.phaseNo || left.bossNo - right.bossNo || left.planIndex - right.planIndex);

  if (!slots.length) {
    return {
      member: { memberOpenid: openid, nickname: textValue(memberAttacks[0]?.nickname) },
      actualAttacks,
      slots: [],
      optionsBySlot: new Map(),
      baseline: null,
      allocations: [],
      contested: [],
      focusAllocation: null,
    };
  }

  // What each boss already has from real attacks and from everyone else's planned slots: this
  // member's own slots are what we are re-arranging, so they are added per allocation instead.
  const slotTargets = [...new Set(slots.map((slot) => slot.planKey))].map((key) => {
    const [bossNo, phaseNo] = key.split(':').map(Number);
    const plan = planList.find((item) => numberValue(item?.bossNo) === bossNo && numberValue(item?.phaseNo) === phaseNo) || null;
    const progress = summarizeRaidStrategyProgress({
      reconciliation: reconcileRaidStrategy({
        plans: plan ? [plan] : [],
        actualRecords: (Array.isArray(actualRecords) ? actualRecords : []).filter((record) => (
          numberValue(record?.bossNo ?? record?.boss_no) === bossNo
          && numberValue(record?.phaseNo ?? record?.phase_no) === phaseNo
        )),
      }),
      targetHpEok: plan?.targetHpEok,
    });
    const memberPlannedEok = progress.activePlans
      .filter((item) => strategyMemberOpenid(item) === openid)
      .reduce((sum, item) => sum + numberValue(item.damageEok), 0);
    return {
      planKey: key,
      bossNo,
      phaseNo,
      isInfinite: isInfiniteRaidTarget(bossNo, phaseNo),
      targetHpEok: progress.targetHpEok,
      baseEok: progress.actualDamageEok + (progress.plannedDamageEok - memberPlannedEok),
    };
  });

  const optionsBySlot = new Map(slots.map((slot) => {
    const options = [slot.current];
    for (const candidate of Array.isArray(candidates) ? candidates : []) {
      if (strategyMemberOpenid(candidate) !== openid) continue;
      if (numberValue(candidate?.bossNo) !== slot.bossNo) continue;
      if (textValue(candidate?.id) === slot.current.id) continue;
      options.push(optionFromCandidate(candidate));
    }
    return [slot.key, options.sort((left, right) => right.damageEok - left.damageEok)];
  }));

  const allocations = enumerateAllocations(slots, optionsBySlot, slotTargets, blockedCodes)
    .sort(compareAllocationRank);
  const baselineKey = slots.map((slot) => `${slot.key}=${slot.current.id}`).join('|');
  const baseline = allocations.find((allocation) => allocation.key === baselineKey)
    || buildAllocation(slots.map((slot) => ({ slot, option: slot.current })), slotTargets);

  const focusSlotKey = textValue(focus?.slotKey);
  const focusOptionId = textValue(focus?.optionId);
  const focusAllocation = focusSlotKey && focusOptionId
    ? allocations.find((allocation) => allocation.optionBySlot.get(focusSlotKey)?.id === focusOptionId) || null
    : null;

  return {
    member: { memberOpenid: openid, nickname: textValue(memberAttacks[0]?.nickname) },
    actualAttacks,
    slots,
    optionsBySlot,
    slotTargets,
    baseline,
    allocations,
    contested: contestedCodeRows({ slots, optionsBySlot, allocations, baseline }),
    focusAllocation,
  };
}

// Moving this member's Nikke changes what the bosses still need, which changes what is worth planning
// for everyone else. To ask that question the plan has to be rewritten as if this member had already
// taken the arrangement, and then handed to the full-raid simulator, which replans the rest.
export function applyAllocationToPlans({ plans = [], slots = [], allocation = null } = {}) {
  const planList = Array.isArray(plans) ? plans : [];
  if (!allocation) return planList;

  const slotByPosition = new Map((Array.isArray(slots) ? slots : [])
    .map((slot) => [`${slot.planKey}#${numberValue(slot.planIndex)}`, slot]));

  return planList.map((plan) => {
    const key = planKey(numberValue(plan?.bossNo), numberValue(plan?.phaseNo));
    const items = Array.isArray(plan?.items) ? plan.items : [];
    let touched = false;
    const nextItems = items.map((item, index) => {
      const slot = slotByPosition.get(`${key}#${index}`);
      const option = slot ? allocation.optionBySlot.get(slot.key) : null;
      if (!slot || !option || option.id === slot.current.id) return item;
      touched = true;
      return {
        ...item,
        practiceId: option.id,
        title: option.title,
        slotNo: option.slotNo,
        damageEok: option.damageEok,
        deck: option.deck,
      };
    });
    return touched ? { ...plan, items: nextItems } : plan;
  });
}

// What the union has already committed: every attack the plan and the real raid have spent, counted
// the way the game records it — capped at each boss's HP, uncapped on the infinite stage.
export function unionCommittedScore({ bosses = [], plans = [], actualRecords = [] } = {}) {
  const targets = strategyPlanTargets({ bosses, plans, actualRecords });
  let effectiveEok = 0;
  let clearedCount = 0;
  for (const target of targets) {
    const hpEok = numberValue(target.hp);
    const projectedEok = numberValue(target.projectedTotalEok);
    const uncapped = isInfiniteRaidTarget(target.bossNo, target.level) || hpEok <= EPSILON;
    effectiveEok += uncapped ? projectedEok : Math.min(projectedEok, hpEok);
    // `targetReached` only means the real raid already killed it. Here the question is whether the
    // plan as written gets there, which is what the simulator is then asked to finish.
    if (!uncapped && projectedEok + EPSILON >= hpEok) clearedCount += 1;
  }
  return { clearedCount, effectiveEok };
}

// What a simulator run adds on top of that. Damage past a boss's remaining HP is never recorded, so
// it comes off rather than counting.
export function unionSimulationScore(result) {
  const best = result?.best || null;
  if (!best) return null;
  const totalEok = numberValue(best.totalDamage);
  const overkillEok = numberValue(best.totalOverkill);
  return {
    clearedCount: numberValue(best.clearedCount),
    totalEok,
    overkillEok,
    usedHits: numberValue(best.usedHits),
    effectiveEok: Math.max(0, totalEok - overkillEok),
  };
}

// The union's whole picture for one arrangement: what is already committed plus what the rest of the
// union would still add. Attacks spent matters on its own — 1~3단 is only the gate, and every attack
// the union does not need there is an attack that lands on the infinite 4단 boss, where the score is
// simply the damage dealt.
export function unionPlacementScore(committed, simulation) {
  if (!committed || !simulation) return null;
  return {
    clearedCount: numberValue(committed.clearedCount) + numberValue(simulation.clearedCount),
    effectiveEok: numberValue(committed.effectiveEok) + numberValue(simulation.effectiveEok),
    spentHits: numberValue(simulation.usedHits),
  };
}

// Reads the difference between two allocations the way the dialog explains it: which boss results
// move, which Nikke moved where, and whether the swap is worth it at all.
export function compareAllocations(baseline, next) {
  if (!baseline || !next) return null;

  const coveredDelta = next.coveredCount - baseline.coveredCount;
  const shortageDelta = next.shortageSumEok - baseline.shortageSumEok;
  const overkillDelta = next.overkillSumEok - baseline.overkillSumEok;
  const damageDelta = next.memberTotalEok - baseline.memberTotalEok;
  const effectiveDelta = next.memberEffectiveEok - baseline.memberEffectiveEok;
  const wastedDelta = next.wastedEok - baseline.wastedEok;

  let verdict = 'even';
  let decidedBy = 'none';
  if (coveredDelta !== 0) {
    verdict = coveredDelta > 0 ? 'gain' : 'loss';
    decidedBy = 'covered';
  } else if (Math.abs(effectiveDelta) > EPSILON) {
    verdict = effectiveDelta > 0 ? 'gain' : 'loss';
    decidedBy = 'effective';
  } else if (Math.abs(shortageDelta) > EPSILON) {
    verdict = shortageDelta < 0 ? 'gain' : 'loss';
    decidedBy = 'shortage';
  } else if (Math.abs(overkillDelta) > EPSILON) {
    verdict = overkillDelta < 0 ? 'gain' : 'loss';
    decidedBy = 'overkill';
  } else if (Math.abs(damageDelta) > EPSILON) {
    verdict = damageDelta > 0 ? 'gain' : 'loss';
    decidedBy = 'damage';
  }

  const targetsByKey = new Map(baseline.targets.map((target) => [target.planKey, target]));
  const slots = next.choices.map((choice) => {
    const before = baseline.optionBySlot.get(choice.slotKey) || null;
    const target = next.targets.find((item) => choice.slotKey.startsWith(`${item.planKey}#`)) || null;
    return {
      slotKey: choice.slotKey,
      before,
      after: choice.option,
      changed: before?.id !== choice.option.id,
      damageDelta: choice.option.damageEok - numberValue(before?.damageEok),
      effectiveDelta: numberValue(target?.memberEffectiveEok)
        - numberValue((target ? targetsByKey.get(target.planKey) : null)?.memberEffectiveEok),
      targetBefore: target ? targetsByKey.get(target.planKey) || null : null,
      targetAfter: target,
    };
  });

  const moved = [];
  for (const [code, slotKey] of next.codePlacement.entries()) {
    const from = baseline.codePlacement.get(code) || '';
    if (from !== slotKey) moved.push({ code, from, to: slotKey });
  }
  for (const [code, slotKey] of baseline.codePlacement.entries()) {
    if (!next.codePlacement.has(code)) moved.push({ code, from: slotKey, to: '' });
  }

  return {
    verdict, decidedBy, coveredDelta, shortageDelta, overkillDelta, damageDelta, effectiveDelta, wastedDelta, slots, moved,
  };
}
